import React, { FC, ReactElement, ReactNode, useMemo } from "react";
import { Box, SvgIconProps, useTheme } from "@mui/material";
import { SizeClass, useWindowSize } from "shared-ts-ui-responsive";
import { PanelGroupStorage } from "react-resizable-panels";
import { AppPagePanelGroupH } from "./horizontal/AppPagePanelGroupH";
import { AppPagePanelGroupV } from "./vertical/AppPagePanelGroupV";
import { AppPageSwipeableBottomPane } from "./mobile/AppPageSwipeableBottomPane";
import { useResizeDetector } from "react-resize-detector";
import { NullError } from "shared-ts-utils-core";
import { IncreasePaperElevation } from "shared-ts-ui-elevation";
import { StringSetting, UserSettings } from "shared-ts-utils-user-settings";
import { Variant } from "./Variant";
import { SupportingPaneProps } from "./shared/SupportingPaneProps";
import { SupportingPaneProvider } from "./shared/SupportingPaneProvider";
import { AppPageLayout } from "./shared/AppPageLayout";
import { MainPaneProps } from "./shared/MainPaneProps";
import { useAppInfo } from "shared-ts-app-info";
import { useUserOrNull } from "shared-ts-utils-authentication";
import { useAppPageVariant } from "./useAppPageVariant";

export interface AppPageProps {
  $key: string;
  name: string;
  variant?: Variant;
  hideName?: boolean;
  icon?: React.ComponentType<SvgIconProps>;
  mainPaneProps?: MainPaneProps;
  mainPaneActions?: ReactNode;
  mainPane: ReactElement;
  supportingPane?: ReactElement | SupportingPaneProvider;
  supportingPaneProps?: SupportingPaneProps;
  disableResize?: boolean;
  layout?: AppPageLayout;
}

export const AppPage: FC<AppPageProps> = (props) => {
  if (props.supportingPane != null && props.supportingPaneProps == null) {
    throw new NullError("supportingPane requires supportingPaneProps");
  }
  const contextVariant = useAppPageVariant();
  const variant = props.variant ?? contextVariant;
  const theme = useTheme();
  const app = useAppInfo();
  const user = useUserOrNull();
  const { windowWidth } = useWindowSize();
  const { width, height, ref } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
  });
  const userSettings = useMemo(
    () => new UserSettings({ app: app.key, user: user }),
    [app.key, user]
  );

  const handlePanelGroupStorage: PanelGroupStorage = {
    getItem: (name: string): string | null => {
      return userSettings.read(name, StringSetting);
    },
    setItem: (name: string, value: string): void => {
      userSettings.write(name, value, StringSetting);
    },
  };

  const mainPane = props.mainPane;

  const layout =
    props.layout != null ? props.layout : resolveLayout(windowWidth);
  const sx = useMemo(() => {
    return {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      "&.compact": {
        flexDirection: "column",
      },
      "&.medium": {
        rowGap: theme.spacing(1),
      },
      "& div[data-panel-group]": {
        flex: 1,
      },
      "& > :first-child /* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */":
        {
          flexGrow: 1,
        },
      "& > aside": {
        display: "flex",
      },
    };
  }, [theme]);

  if (layout === "SwipeableBottomPane") {
    return (
      <Box className={"AppPage compact"} ref={ref} sx={sx}>
        <IncreasePaperElevation>
          <AppPageSwipeableBottomPane
            $key={props.$key}
            name={props.name}
            width={width}
            height={height}
            variant={variant}
            hideName={props.hideName}
            icon={props.icon}
            mainPaneProps={props.mainPaneProps}
            mainPaneActions={props.mainPaneActions}
            supportingPaneProps={props.supportingPaneProps}
            supportingPane={props.supportingPane}
            disableResize={props.disableResize}
            onPanelGroupStorage={handlePanelGroupStorage}
          >
            {mainPane}
          </AppPageSwipeableBottomPane>
        </IncreasePaperElevation>
      </Box>
    );
  } else if (layout === "PanelGroupVertical") {
    return (
      <Box className={"AppPage medium"} ref={ref} sx={sx}>
        <IncreasePaperElevation>
          <AppPagePanelGroupV
            $key={props.$key}
            name={props.name}
            width={width}
            height={height}
            variant={variant}
            hideName={props.hideName}
            icon={props.icon}
            mainPaneProps={props.mainPaneProps}
            mainPaneActions={props.mainPaneActions}
            supportingPaneProps={props.supportingPaneProps}
            supportingPane={props.supportingPane}
            disableResize={props.disableResize}
            onPanelGroupStorage={handlePanelGroupStorage}
          >
            {mainPane}
          </AppPagePanelGroupV>
        </IncreasePaperElevation>
      </Box>
    );
  }
  return (
    <Box className={"AppPage"} ref={ref} sx={sx}>
      <IncreasePaperElevation>
        <AppPagePanelGroupH
          $key={props.$key}
          name={props.name}
          width={width}
          height={height}
          variant={variant}
          hideName={props.hideName}
          icon={props.icon}
          maxWidth={width}
          mainPaneProps={props.mainPaneProps}
          mainPaneActions={props.mainPaneActions}
          supportingPaneProps={props.supportingPaneProps}
          supportingPane={props.supportingPane}
          disableResize={props.disableResize}
          onPanelGroupStorage={handlePanelGroupStorage}
        >
          {mainPane}
        </AppPagePanelGroupH>
      </IncreasePaperElevation>
    </Box>
  );
};

const resolveLayout = (width: SizeClass): AppPageLayout => {
  if (width === SizeClass.Compact) {
    return "SwipeableBottomPane";
  } else if (width === SizeClass.Medium) {
    return "PanelGroupVertical";
  } else {
    return "PanelGroupHorizontal";
  }
};
